import { type GridApiRef, useGridApiRef } from '@mui/x-data-grid';
import { keepPreviousData } from '@tanstack/react-query';
import { useDebounce } from '@uidotdev/usehooks';
import { memo, useCallback, useEffect, useMemo } from 'react';

import { useQueryCustomFields } from '~/data/customField';
import { type DeliveryNotePdfExportFormat } from '~/data/dataExport';
import {
  fetchExportedReport,
  getReportConfiguration,
  getReportOrder,
  useMutationRequestDeliveryNoteExcel,
  useQueryDeliveryNoteReport,
} from '~/data/deliveryNote';

import {
  setDashboard_selectableColumns,
  setDashboard_selectedColumns,
} from '~/redux/filtersSlice';
import { type RootState, useDispatch, useSelector } from '~/redux/store';

import ExportService from '~/services/export.service';
import FeatureService from '~/services/feature.service';

import { CustomFieldObject } from '~/models/customData';

import { clone, moveItem, uniqueBy } from '@vestigas/utils';
import { LOG, log } from '~/utils/logging';

import { ClientPortalMessage } from '~/components/salesPackages/clientPortal/ClientPortalMessage';
import { PackageBasicRestrictionMessage } from '~/components/salesPackages/packageBasicRestriction/PackageBasicRestrictionMessage';
import { withErrorBoundary } from '~/ui/atoms';
import { CustomFieldValue } from '~/ui/molecules/CustomFieldValue';
import { DatagridServerDriven } from '~/ui/molecules/Datagrid';

import { DashboardReportChips } from '../DashboardReportChips';

import { SELECTABLE_COLUMNS } from './constants';
import { getTotalRowCount } from './getTotalRowCount';
import { selectDashboardReportData } from './selectDashboardReportData';
import { useDashboardPagination } from './useDashboardPagination';
import { useReportColumns } from './useReportColumns';
import { useReportSortingModel } from './useReportSorting';

const FILTER_AND_COLUMN_CHANGE_DEBOUNCE_TIME = 800;

const selectSelectableColumns = (state: RootState) =>
  state.filters.dashboard_selectableColumns;
const selectSelectedColumns = (state: RootState) =>
  state.filters.dashboard_selectedColumns;
const selectSelectedUnit = (state: RootState) =>
  state.filters.dashboard_selectedUnit;

// Defined outside the component so it isn't mistaken for a nested component
// definition by react-hooks lint rules (it's a plain DataGrid cell renderer).
const renderCustomFieldCell =
  (customField: Record<string, any>) =>
  ({ value }: { value: unknown }) => (
    <CustomFieldValue
      value={CustomFieldObject.getGridDisplayValue(value, customField)}
    />
  );

type P = {
  searchBody: any;
  predefinedDateRange: string;
  dateRange: Date[];
  onPdfExport: (format: DeliveryNotePdfExportFormat, filename: string) => void;
};

// The report's queries, column state and export toolbar all depend on the same
// filter and column selection, so they live in one component.
// fallow-ignore-next-line complexity
function DashboardReportContent({
  searchBody,
  predefinedDateRange,
  dateRange,
  onPdfExport,
}: P) {
  const apiRef = useGridApiRef() as GridApiRef;

  const dispatch = useDispatch();
  const selectedUnit = useSelector(selectSelectedUnit);
  const selectableColumns = useSelector(selectSelectableColumns);
  const selectedColumns = useSelector(selectSelectedColumns);

  const { mutateAsync: requestExport } = useMutationRequestDeliveryNoteExcel();

  function getPdfExportFilename(format: DeliveryNotePdfExportFormat) {
    return ExportService.generateFileName({
      date:
        predefinedDateRange === 'individual'
          ? ExportService.generateDateStringFromDateRange(dateRange)
          : undefined,
      entityType: ExportService.ENTITIES.DELIVERY_NOTES,
      format,
      predefinedRange: predefinedDateRange,
    });
  }

  const { data: customFieldsData, isLoading: isLoadingCustomFields } =
    useQueryCustomFields({}, { includeOptions: true });
  const customFields = customFieldsData ?? [];

  const { paginationModel, handlePaginationModelChange } =
    useDashboardPagination(searchBody);

  const { sortingModel, handleSortingModelChange } = useReportSortingModel();

  const reportConfig = useMemo(
    () => getReportConfiguration(selectedColumns, customFields),
    [selectedColumns, customFields],
  );
  const reportOrder = useMemo(
    () => getReportOrder(selectedColumns, customFields),
    [selectedColumns, customFields],
  );
  const searchParamsObject = useMemo(
    () => ({
      ...searchBody,
      limit: paginationModel.pageSize,
      offset: paginationModel.page * paginationModel.pageSize,
      orderBy: sortingModel.orderBy,
      reportConfig,
      reportOrder,
      sortOrder: sortingModel.sortOrder,
      unitType: selectedUnit || null,
    }),
    [
      paginationModel.page,
      paginationModel.pageSize,
      reportConfig,
      reportOrder,
      searchBody,
      selectedUnit,
      sortingModel.orderBy,
      sortingModel.sortOrder,
    ],
  );

  const searchParams = useDebounce(
    searchParamsObject,
    FILTER_AND_COLUMN_CHANGE_DEBOUNCE_TIME,
  );

  const {
    data: reportData,
    isFetching: isFetchingReportData,
    isLoading: isLoadingReportData,
    isPlaceholderData,
  } = useQueryDeliveryNoteReport(searchParams, {
    enabled:
      !isLoadingCustomFields && Boolean(searchParams?.reportConfig?.length > 0),
    placeholderData: keepPreviousData,
    select: selectDashboardReportData,
  });

  const { getColumns } = useReportColumns({
    isLoading: isFetchingReportData,
    reportData,
    selectableColumns,
    selectedColumns,
  });

  useEffect(() => {
    if (!customFields) {
      return;
    }

    const columns =
      selectableColumns.length > 0
        ? clone(selectableColumns)
        : clone(SELECTABLE_COLUMNS);

    for (const customField of customFields) {
      columns.push({
        disableReorder: true,
        field: customField.key,
        headerName: customField.displayName,
        renderCell: renderCustomFieldCell(customField),
        resizable: true,
        sortable: true,
        valueFormatter: (value: unknown) =>
          CustomFieldObject.getGridDisplayValue(value, customField),
      });
    }

    dispatch(
      setDashboard_selectableColumns(uniqueBy(columns, ({ field }) => field)),
    );

    // Only reorder selectedColumns on initial load if they exist in localStorage
    if (selectedColumns.length === 0) {
      const fields = columns.map(({ field }) => field);
      const reorderedSelectedColumns = selectedColumns
        .filter((field) => fields.includes(field))
        .sort((a, b) => fields.indexOf(a) - fields.indexOf(b));

      // Detect changes in content and order
      const hasChanges =
        JSON.stringify(reorderedSelectedColumns) !==
        JSON.stringify(selectedColumns);

      if (hasChanges) {
        dispatch(setDashboard_selectedColumns(reorderedSelectedColumns));
      }
    }
  }, [JSON.stringify(customFields)]);

  const handleExportExcelFromBackend = async () => {
    const searchParams = {
      ...searchBody,
      reportConfig,
      reportOrder,
      unitType: selectedUnit || null,
    };

    const response = await requestExport(searchParams);

    const data = await fetchExportedReport(response.requestId);

    ExportService.exportExcelFromBlob(data, {
      customRange: dateRange,
      entityType: ExportService.ENTITIES.REPORT,
      predefinedRange: predefinedDateRange,
    });
  };

  const handleToggleColumnIncludedState = useCallback(
    (clickedField) => {
      log.productAnalyticsEvent('(De)select column', LOG.FEATURE.REPORT);

      let newSelectedFields = [...selectedColumns];

      if (selectedColumns.includes(clickedField)) {
        newSelectedFields = newSelectedFields.filter(
          (field) => field !== clickedField,
        );
      } else {
        // Insert the clickedField at same relative position as in selectableColumns
        const orderedFields = selectableColumns.map(({ field }) => field);

        const insertIndex = newSelectedFields.findIndex(
          (field) =>
            orderedFields.indexOf(field) > orderedFields.indexOf(clickedField),
        );

        if (insertIndex === -1) {
          newSelectedFields.push(clickedField);
        } else {
          newSelectedFields.splice(insertIndex, 0, clickedField);
        }
      }

      dispatch(setDashboard_selectedColumns(newSelectedFields));
    },
    [selectedColumns, selectableColumns, dispatch],
  );

  const handleReorderColumns = useCallback(
    (sourceIndex, destinationIndex) => {
      log.productAnalyticsEvent('Reorder columns', LOG.FEATURE.REPORT);

      let newSelectableColumns = clone(selectableColumns);
      newSelectableColumns = moveItem(
        newSelectableColumns,
        sourceIndex,
        destinationIndex,
      );
      dispatch(setDashboard_selectableColumns(newSelectableColumns));

      const sortedColumns = newSelectableColumns.map(({ field }) => field);
      dispatch(
        setDashboard_selectedColumns(
          sortedColumns.filter((column) => selectedColumns.includes(column)),
        ),
      );
    },
    [selectableColumns, selectedColumns, dispatch],
  );

  const toolbarItems = [
    {
      actions: [
        {
          name: 'Daten exportieren (Excel)',
          onClick() {
            handleExportExcelFromBackend().catch((error) => {
              console.error('Failed to export Excel:', error);
            });
          },
        },
      ],
      icon: 'excel',
      id: 'export-xlsx-server-side',
      title: 'Daten exportieren (Excel)',
    },
    {
      actions: [
        {
          name: 'Als ein zusammengefasstes PDF',
          onClick() {
            onPdfExport('pdf', getPdfExportFilename('pdf'));
          },
        },
        {
          name: 'Als zip-File mit einzelnen PDFs',
          onClick() {
            onPdfExport('zip', getPdfExportFilename('zip'));
          },
        },
      ],
      icon: 'pdf',
      id: 'export-pdf-server-side',
      title: 'PDFs exportieren',
    },
  ];

  const { page, pageSize } = paginationModel;

  const paginationText = `${page * pageSize + 1}-${Math.min(
    (page + 1) * pageSize,
    page * pageSize + ((reportData?.paginatedCount as number) ?? 0),
  )}`;
  const totalRowCount = getTotalRowCount({
    dataLength: reportData?.data?.length ?? 0,
    hasNextPage: reportData?.hasNextPage ?? false,
    isPlaceholderData,
    page,
    pageSize,
  });

  if (FeatureService.clientPortal()) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <ClientPortalMessage />
      </div>
    );
  }

  if (FeatureService.packageBasicRestriction()) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <PackageBasicRestrictionMessage />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col">
      <div className="py-2">
        <DashboardReportChips
          selectableColumns={selectableColumns}
          selectedColumns={selectedColumns}
          onClick={handleToggleColumnIncludedState}
          onReorderColumns={handleReorderColumns}
        />
      </div>
      {selectedColumns.length === 0 ? (
        <div className="pb-2">
          * Bitte wähle mindestens eine Spalte (Status, LFS-Datum, ...) aus, um
          die Mengenaufstellungs-Funktion zu verwenden.
        </div>
      ) : null}
      <div className="h-[600px] overflow-x-auto">
        <DatagridServerDriven
          apiRef={apiRef}
          checkboxSelection={false}
          columns={getColumns()}
          isLoading={isLoadingReportData || isFetchingReportData}
          paginationMeta={{ totalRowCount }}
          paginationModel={paginationModel}
          paginationText={paginationText}
          rows={reportData?.data ?? []}
          sortingMode="server"
          toolbarItems={toolbarItems}
          onPaginationModelChange={handlePaginationModelChange}
          onSortModelChange={handleSortingModelChange}
        />
      </div>
    </div>
  );
}

export const DashboardReport = memo(
  withErrorBoundary(
    DashboardReportContent,
    'Berichts-Daten konnten nicht geladen werden.',
  ),
);

DashboardReport.displayName = 'DashboardReport';
